import type { Character } from '../domain/character.ts';
import type { SessionState } from '../domain/session.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inspectRuntimeRecord } from './runtimeCompatibility.ts';
import type { LegRefusalKind } from './recordCompatibility.ts';
import { inspectCharacterRecord, inspectSessionRecord } from './recordCompatibility.ts';
import type { RuntimeRepository } from './runtimeRepository.ts';
import type { CharacterRepository } from './characterRepository.ts';
import type { SessionRepository } from './sessionRepository.ts';
import type { StoragePort } from './storagePort.ts';
import { WriteQueue } from './writeQueue.ts';

/**
 * Commits an advancement checkpoint, which changes TWO files at once: the
 * character (Level, Skill Ranks, marks) and the session (roll history, pending
 * award).
 *
 * Two independent writes can leave the player with an advanced character and a
 * stale history, or a history full of advancement rolls that never happened.
 * A tiny write-ahead journal removes that window without adding a database:
 *
 *   1. write the journal, holding BOTH intended records;
 *   2. write the character;
 *   3. write the session;
 *   4. delete the journal.
 *
 * A failure at step 2, 3 or 4 leaves the journal in place. `recover()` replays
 * it on next load, so the checkpoint completes rather than staying half-applied.
 * The journal is written atomically, so it is never itself half-written.
 *
 * TWO PROPERTIES THIS ALSO HAS TO HAVE, because E1 turned it from an occasional
 * advancement checkpoint into the pathway every Attack goes through:
 *
 *  - **One transaction per crawler at a time.** There is one journal per
 *    crawler, so two overlapping commits would have the second overwrite the
 *    first's journal and destroy the only record of what the first intended.
 *    A double-click is enough to reach that. `commit` and `recover` share one
 *    per-character queue covering the WHOLE transaction, not the individual
 *    repository writes - those are already ordered, and ordering is not the
 *    problem here.
 *  - **A pending journal is never overwritten.** A commit that finds one
 *    refuses. It does NOT recover and then continue, because the caller's next
 *    states were computed from the PRE-recovery records and would silently
 *    undo the operation that was waiting to land. The caller must reload.
 */
export const CHECKPOINT_JOURNAL_VERSION = 1;

/**
 * Why a checkpoint was refused. The distinction matters to the caller: two of
 * these prove NOTHING was written and no journal was created, so the screen can
 * simply say so and carry on. The third means a transaction is waiting to land
 * and the in-memory records are no longer authoritative.
 */
export type CheckpointRefusalKind =
  /** Another transaction for this crawler is in flight. Nothing was written. */
  | 'busy'
  /** A previous transaction is waiting for recovery. Nothing was written. */
  | 'pending-recovery'
  /**
   * A real transaction is waiting that this build cannot replay - a journal
   * written in a newer format. Nothing was written, and nothing can be until
   * the newer build finishes it.
   */
  | 'unreadable-journal'
  /** The records handed in do not all belong to one crawler. Nothing written. */
  | 'identity';

export class CheckpointRefusal extends Error {
  readonly kind: CheckpointRefusalKind;
  /**
   * Which leg could not be read, and why, when `kind` is 'unreadable-journal'.
   *
   * Present so a caller - and a test - can tell a future Session from a
   * malformed Character without matching on prose. Absent for the other kinds,
   * which are not about a leg.
   */
  readonly cause?: JournalBlockCause;

  constructor(kind: CheckpointRefusalKind, message: string, cause?: JournalBlockCause) {
    super(message);
    this.name = 'CheckpointRefusal';
    this.kind = kind;
    this.cause = cause;
  }
}

/**
 * Whether a failed `commit()` may have left a pending journal, so the caller
 * must stop writing and recover before doing anything else.
 *
 * THIS DECISION BELONGS HERE, not in the screen. The committer is the only
 * thing that knows whether it got as far as writing the journal. Anything it
 * did not refuse outright is assumed to have reached the disk, because failing
 * closed costs a reload and failing open costs the player's work.
 */
export function requiresRecovery(error: unknown): boolean {
  // A failed ATOMIC write never created a journal, so there is nothing waiting
  // to be recovered. The caller reloads and retries like any single-record save.
  if (typeof error === 'object' && error !== null && atomicWriteFailures.has(error)) return false;
  if (error instanceof CheckpointRefusal) {
    return error.kind === 'pending-recovery' || error.kind === 'unreadable-journal';
  }
  return true;
}

/**
 * Errors thrown by a store's `writeTogether`. Kept by identity rather than by
 * wrapping, so the host still receives ITS OWN error (stale, signed out,
 * offline) and can say the right thing to the player.
 */
const atomicWriteFailures = new WeakSet<object>();

/** Which leg of a transaction a block is about. */
export type JournalLeg = 'character' | 'session' | 'runtime';

/**
 * Why a journal is blocked, in a form a caller can branch on.
 *
 * Deliberately NOT persisted into the journal. It is a fact about THIS BUILD's
 * ability to read the record, not a fact about the record, and writing it down
 * would make one build's limitation look like part of the saved transaction.
 */
export interface JournalBlockCause {
  leg: JournalLeg;
  kind: LegRefusalKind | 'newer-encounter';
}

function isRecordObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export interface CheckpointJournal {
  version: number;
  characterId: string;
  writtenAt: string;
  character: Character;
  session: SessionState;
  /** Present when the operation also changed the crawler's runtime state. */
  runtime?: RuntimeState | null;
}

export interface CommitResult {
  character: Character;
  session: SessionState;
  runtime?: RuntimeState | null;
}

/**
 * Every record in one transaction must belong to the same crawler.
 *
 * The journal is keyed on the CHARACTER's id, so a Session or Runtime carrying
 * a different `characterId` would be journalled under crawler A and written to
 * crawler B's file - and recovery would then faithfully replay the mix. Refused
 * before any write, and refused HERE rather than in each caller, because a
 * caller that forgets is exactly the failure this guards against.
 */
function assertOneCrawler(
  character: Character,
  session: SessionState,
  runtime?: RuntimeState | null,
): void {
  if (session.characterId !== character.id) {
    throw new CheckpointRefusal(
      'identity',
      `Internal fault: this save mixes crawlers. The session belongs to ${session.characterId} ` +
        `but the character is ${character.id}. Nothing has been changed.`,
    );
  }
  if (runtime && runtime.characterId !== character.id) {
    throw new CheckpointRefusal(
      'identity',
      `Internal fault: this save mixes crawlers. The runtime state belongs to ` +
        `${runtime.characterId} but the character is ${character.id}. Nothing has been changed.`,
    );
  }
}

export class CheckpointCommitter {
  private readonly storage: StoragePort;
  private readonly characters: CharacterRepository;
  private readonly sessions: SessionRepository;
  private readonly now: () => string;
  private readonly runtimes: RuntimeRepository;
  /** One chain per crawler, covering the whole transaction. */
  private readonly queue = new WriteQueue();
  /**
   * Crawlers with a transaction in flight RIGHT NOW. Not a queue: a second
   * commit for a crawler already in here is refused outright. See `commit`.
   */
  private readonly active = new Map<string, number>();

  constructor(
    storage: StoragePort,
    characters: CharacterRepository,
    sessions: SessionRepository,
    now: () => string = () => new Date().toISOString(),
    // REQUIRED. It used to be optional, which allowed a caller to pass a
    // RuntimeState with no repository to write it to: the committer skipped the
    // runtime write, deleted the journal, and returned as though a three-record
    // transaction had succeeded. Silently losing one third of a transaction is
    // exactly what a journal exists to prevent.
    runtimes: RuntimeRepository,
  ) {
    this.storage = storage;
    this.characters = characters;
    this.sessions = sessions;
    this.now = now;
    this.runtimes = runtimes;
  }

  /**
   * Commits a character and session, and optionally the runtime state, as one
   * journalled operation. An advancing round changes all three, and none of
   * them may land without the others.
   *
   * A SECOND CONCURRENT TRANSACTION FOR THE SAME CRAWLER IS REFUSED, NOT
   * QUEUED. Queueing is the right answer for low-level writes, which is what
   * `WriteQueue` is for. It is the wrong answer here, because callers hand this
   * method an ALREADY-COMPUTED next state:
   *
   *   Attack A resolves from 2 Actions / 10 Mana -> 1 Action, 9 Mana, history A.
   *   Attack B resolves from the SAME records    -> 1 Action, 9 Mana, history B.
   *   A commits. B then commits from the queue and overwrites it.
   *
   * Both Attacks succeeded, one Action and one Mana were spent, and only B's
   * roll is in the history. Serializing the WRITES does not make the
   * read-modify-write pair logically serial, because the read happened in the
   * caller before either write started. The only safe answer is to refuse the
   * second submission so its stale next-state is never written at all.
   *
   * Ownership is claimed SYNCHRONOUSLY, before the first `await`, so two calls
   * made in the same tick cannot both see a free crawler. It is released in
   * `finally`, including on failure - after which the pending-journal rule
   * below still refuses the next commit until recovery has run.
   *
   * Deliberately NOT solved with `updatedAt` as an optimistic version: two
   * saves can legitimately carry the same millisecond, and a timestamp is not
   * a transaction revision.
   */
  async commit(
    character: Character,
    session: SessionState,
    runtime?: RuntimeState | null,
  ): Promise<CommitResult> {
    // THE TRANSACTION BOUNDARY IS THE RIGHT PLACE FOR THIS INVARIANT. Without
    // it the journal is written under crawler A while the Session or Runtime
    // records belonging to crawler B are saved, and recovery then replays a
    // crawler's file into someone else's. Refused before any write, and before
    // ownership is claimed: a mismatch is an internal fault, not a queueing
    // question.
    assertOneCrawler(character, session, runtime);

    const id = character.id;
    if (this.isActive(id)) {
      throw new CheckpointRefusal(
        'busy',
        'Another change to this crawler is still saving. Nothing has been changed; ' +
          'wait for it to finish and try again.',
      );
    }
    this.enter(id);
    try {
      return await this.queue.run(`checkpoint:${id}`, () =>
        this.commitOnce(character, session, runtime),
      );
    } finally {
      this.leave(id);
    }
  }

  private async commitOnce(
    character: Character,
    session: SessionState,
    runtime?: RuntimeState | null,
  ): Promise<CommitResult> {
    // An earlier transaction is still waiting to land. Its journal holds the
    // only record of what it intended, so it is not overwritten and not
    // silently recovered-then-superseded: the caller's states are stale and
    // writing them would undo the pending operation.
    const pending = await this.readJournal(character.id);
    if (pending.state === 'blocked') {
      throw new CheckpointRefusal('unreadable-journal', pending.reason, pending.cause);
    }
    if (pending.state === 'ready') {
      throw new CheckpointRefusal(
        'pending-recovery',
        'A previous save for this crawler did not finish. Reload the crawler to complete it ' +
          'before making another change. Nothing has been changed.',
      );
    }

    // A store that can write all the records as ONE transaction needs no
    // journal: the write cannot be half-applied, so there is never anything to
    // finish later. This is also the only safe behaviour on a shared store. A
    // journal left behind by a REFUSED write (someone else saved first) would
    // be replayed on the next load, over the other writer's change.
    if (this.storage.writeTogether) {
      return this.commitTogether(character, session, runtime ?? null);
    }

    const journal: CheckpointJournal = {
      version: CHECKPOINT_JOURNAL_VERSION,
      characterId: character.id,
      writtenAt: this.now(),
      character,
      session,
      runtime: runtime ?? null,
    };
    await this.storage.writeJournal(character.id, journal);

    const savedCharacter = await this.characters.save(character);
    const savedSession = await this.sessions.save(session);
    // The SAVED runtime is returned, not the object handed in: the repository
    // stamps `updatedAt`, and giving the caller the unstamped original would
    // hand back a record that does not match what is on disk.
    const savedRuntime = runtime ? await this.runtimes.save(runtime) : null;

    // All of them landed. The journal is no longer needed.
    await this.storage.deleteJournal(character.id);
    return { character: savedCharacter, session: savedSession, runtime: savedRuntime };
  }

  /**
   * The atomic path. The records are stamped by the SAME repositories the
   * journalled path saves through, so what lands is identical; only the number
   * of writes differs (one instead of five or six).
   */
  private async commitTogether(
    character: Character,
    session: SessionState,
    runtime: RuntimeState | null,
  ): Promise<CommitResult> {
    const writeTogether = this.storage.writeTogether!.bind(this.storage);
    const stampedCharacter = this.characters.stamp(character);
    const stampedSession = this.sessions.stamp(session);
    const stampedRuntime = runtime ? this.runtimes.stamp(runtime) : null;
    try {
      await this.characters.writeWithIndex(stampedCharacter, (index) =>
        writeTogether({
          characterId: stampedCharacter.id,
          character: stampedCharacter,
          session: stampedSession,
          ...(stampedRuntime ? { runtime: stampedRuntime } : {}),
          index,
        }),
      );
    } catch (error) {
      if (typeof error === 'object' && error !== null) atomicWriteFailures.add(error);
      throw error;
    }
    return { character: stampedCharacter, session: stampedSession, runtime: stampedRuntime };
  }

  /** True when a checkpoint was interrupted and is waiting to be finished. */
  async hasPending(characterId: string): Promise<boolean> {
    return (await this.readJournal(characterId)).state !== 'none';
  }

  /**
   * Finishes an interrupted checkpoint. Safe to call on every load and safe to
   * call twice: replaying the same records is idempotent.
   */
  async recover(characterId: string): Promise<CommitResult | null> {
    // Recovery TAKES OWNERSHIP TOO, but never refuses: it is idempotent, and it
    // is called on every load, so two overlapping loads must both be allowed to
    // finish rather than one erroring. What ownership buys here is the other
    // direction - a `commit` arriving mid-recovery is refused instead of
    // queueing behind it and then writing a next state that was computed from
    // the PRE-recovery records, which is the exact "recover then supersede"
    // this class exists to prevent.
    this.enter(characterId);
    try {
      // The SAME queue as `commit`, so recovery cannot run alongside a
      // transaction for the same crawler and read a journal that is mid-write.
      return await this.queue.run(`checkpoint:${characterId}`, () => this.recoverOnce(characterId));
    } finally {
      this.leave(characterId);
    }
  }

  /** Crawlers with an owned transaction in flight, counted so overlapping
   *  recoveries do not release each other's ownership early. */
  private isActive(characterId: string): boolean {
    return (this.active.get(characterId) ?? 0) > 0;
  }

  private enter(characterId: string): void {
    this.active.set(characterId, (this.active.get(characterId) ?? 0) + 1);
  }

  private leave(characterId: string): void {
    const remaining = (this.active.get(characterId) ?? 0) - 1;
    if (remaining <= 0) this.active.delete(characterId);
    else this.active.set(characterId, remaining);
  }

  private async recoverOnce(characterId: string): Promise<CommitResult | null> {
    const pending = await this.readJournal(characterId);
    // A journal this build cannot replay is NOT quietly skipped. Reporting
    // "nothing to recover" would hand the caller a sheet it is then free to
    // write over the top of a transaction that is still owed.
    if (pending.state === 'blocked') {
      throw new CheckpointRefusal('unreadable-journal', pending.reason, pending.cause);
    }
    if (pending.state === 'none') return null;
    const journal = pending.journal;

    const savedCharacter = await this.characters.save(journal.character);
    const savedSession = await this.sessions.save(journal.session);
    const savedRuntime = journal.runtime ? await this.runtimes.save(journal.runtime) : null;
    // Deleted LAST, and only once all three have landed. A failure above leaves
    // it in place so the next load tries again.
    await this.storage.deleteJournal(characterId);
    return { character: savedCharacter, session: savedSession, runtime: savedRuntime };
  }

  /**
   * What is sitting in the journal slot for this crawler.
   *
   * Three outcomes, and the difference matters. `none` means nothing is owed -
   * either there was no journal or there was rubbish, which has been deleted.
   * `ready` means a real transaction is waiting and can be replayed. `blocked`
   * means a real transaction is waiting that THIS BUILD CANNOT REPLAY, which is
   * neither safe to run nor safe to throw away.
   */
  private async readJournal(
    characterId: string,
  ): Promise<
    | { state: 'none' }
    | { state: 'ready'; journal: CheckpointJournal }
    | { state: 'blocked'; reason: string; cause?: JournalBlockCause }
  > {
    const raw = await this.storage.readJournal(characterId);
    if (raw === null || typeof raw !== 'object') return { state: 'none' };

    const record = raw as Record<string, unknown>;

    /** Rubbish. Deleted rather than left to refuse every future save forever. */
    const discard = async (): Promise<{ state: 'none' }> => {
      await this.storage.deleteJournal(characterId);
      return { state: 'none' };
    };

    // A journal filed under this crawler that claims to belong to someone else
    // is garbage for this key. It used to be ignored and left on disk, where it
    // sat forever doing nothing.
    if (record['characterId'] !== characterId) return discard();

    // WRITTEN BY A NEWER BUILD. Its shape is not known here, so replaying it
    // would mean guessing, and deleting it would throw away real work. Neither
    // is acceptable, so the crawler stays shut until the newer build finishes
    // what it started.
    const version = record['version'];
    if (version !== CHECKPOINT_JOURNAL_VERSION) {
      return {
        state: 'blocked',
        reason:
          `This crawler has an unfinished save written in journal format ${String(version)}, ` +
          `but this version of the application only understands format ` +
          `${CHECKPOINT_JOURNAL_VERSION}. Open it with the newer version to finish the save. ` +
          `Nothing has been changed.`,
      };
    }

    // CLASSIFY EVERY LEG BEFORE BUILDING A REPLAYABLE JOURNAL.
    //
    // Nothing below returns a `ready` journal until all three legs have been
    // inspected, and `recoverOnce` writes nothing until it holds one. That
    // ordering is the guarantee that a block can never half-apply the very
    // transaction it exists to protect.
    //
    // A REQUIRED LEG THAT IS SIMPLY NOT THERE is not an unreadable leg. A
    // checkpoint journal always carries a character and a session; a record
    // missing one, or holding a primitive where a record belongs, is not a
    // recognizable transaction at all and stays disposable. The runtime is the
    // one optional leg - a two-record checkpoint is legitimate.
    if (!isRecordObject(record['character']) || !isRecordObject(record['session'])) {
      return discard();
    }

    // The runtime carries one kind the other two cannot - `newer-encounter` -
    // so the classification list is typed on the union rather than on
    // `LegRefusalKind` alone. `JournalBlockCause['kind']` is that union, and
    // naming it once here is what keeps the three inspectors from having to be
    // merged into one to share a type.
    type LegOutcome =
      | { ok: true }
      | { ok: false; kind: JournalBlockCause['kind']; message: string };
    const legs: Array<{ leg: JournalLeg; inspection: LegOutcome }> = [];
    const characterLeg = inspectCharacterRecord(record['character'], characterId);
    legs.push({ leg: 'character', inspection: characterLeg });
    const sessionLeg = inspectSessionRecord(record['session'], characterId);
    legs.push({ leg: 'session', inspection: sessionLeg });

    // THE RUNTIME LEG IS PRESENT-OR-ABSENT, never "present but ignored".
    // Absent is a legitimate two-record transaction. Present but unreadable is
    // a DAMAGED three-record one, and replaying it as though it had only ever
    // had two would quietly drop a leg while reporting success.
    const runtimeSupplied = record['runtime'] !== undefined && record['runtime'] !== null;
    const runtimeLeg = runtimeSupplied
      ? inspectRuntimeRecord(record['runtime'], characterId)
      : null;
    if (runtimeLeg !== null) legs.push({ leg: 'runtime', inspection: runtimeLeg });

    const failed = legs.find((entry) => !entry.inspection.ok);
    if (failed !== undefined) {
      const inspection = failed.inspection as { kind: JournalBlockCause['kind']; message: string };
      return {
        state: 'blocked',
        reason:
          `This crawler has an unfinished save whose ${failed.leg} state this version of ` +
          `the application cannot safely read. Nothing has been changed. ` +
          `(${inspection.message})`,
        cause: { leg: failed.leg, kind: inspection.kind },
      };
    }

    const character = (characterLeg as { ok: true; record: Character }).record;
    const session = (sessionLeg as { ok: true; record: SessionState }).record;
    const runtime =
      runtimeLeg === null ? null : (runtimeLeg as { ok: true; runtime: RuntimeState }).runtime;

    return {
      state: 'ready',
      journal: {
        version: CHECKPOINT_JOURNAL_VERSION,
        characterId,
        writtenAt: String(record['writtenAt'] ?? ''),
        character: character as Character,
        session: session as SessionState,
        runtime,
      },
    };
  }
}
